var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useRef } from 'react';
import styled, { css } from 'styled-components';
import { deprecated } from '@ally/metronome-icons';
import { v4 as uuidv4 } from 'uuid';
import { Icon } from '../../Icon';
import { TextBody, TextHeading } from '../../Typography';
import { Box } from '../../Box';
import { get } from '../../tools/utils';
import { ScreenReaderOnly } from '../../ScreenReaderOnly';
import { addSuperscript } from '../../tools/utils/addSuperscript';
const StyledRow = styled.div `
  ${({ theme: { font } }) => `
    line-height: ${font.body.sm.lineHeight};
  `}
`;
const AccordionHeaderContainer = styled(Box) `
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { space } }) => space.xxs};
`;
const IconWrapper = styled.div `
  ${({ theme: { font } }) => `
    line-height: ${font.body.sm.lineHeight};
    height: ${font.body.sm.lineHeight};
    display: flex;
    align-items: center;
  `}
`;
const StyledIcon = styled(Icon) `
  ${({ theme: { iconSize } }) => `
    width: ${iconSize.xl};
    min-width: ${iconSize.xl};
  `}
`;
const StyledHeading = styled(TextHeading)(({ theme: { font, iconSize } }) => css `
    display: inline-block;
    font-weight: bold;
    font-size: ${font.body.sm.size};
    line-height: ${font.body.sm.lineHeight};
    font-family: ${get.fonts('regular')};
    color: ${get.colors('bluesuedeshoes')};
    width: fit-content;
    padding-right: ${iconSize.xl};
  `);
const StyledTextBody = styled(TextBody) `
  ${({ theme: { iconSize } }) => `
    flex: 1;
    padding-left: ${iconSize.xl};
    padding-right: ${iconSize.xl};
  `}
`;
const CustomButton = styled.button `
  display: flex;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  overflow: hidden;
`;
const AccordionHeader = styled.div `
  ${({ theme: { colors, font, fonts, space, media }, $disableContainer, }) => `
    color: ${colors.bluesuedeshoes};
    font-size: ${font.heading.sm.size};
    font-family: ${fonts.regular};

    ${$disableContainer
    ? `
            padding: ${space.sm} ${space.xs};
            box-sizing: border-box;
          `
    : `
            background-color: ${colors.white};
            padding: ${space.sm};
            padding-left: ${space.xs};
            border-bottom: 1px solid ${colors['slate-2']};
            box-sizing: border-box;
          `}
  `}

  line-height: 1.25;
  width: 100%;
  text-align: left;
  font-weight: bold;
  box-sizing: border-box;
`;
const AccordionItemHeaderBaseRefForwarding = (_a, ref) => {
    var _b, _c;
    var { heading, headingLevel, description, isExpanded, disableContainer = false, onExpandedChange, accordionItemKey, clickEventName, dataTracking, ariaDescribedBy, onHeaderClick } = _a, rest = __rest(_a, ["heading", "headingLevel", "description", "isExpanded", "disableContainer", "onExpandedChange", "accordionItemKey", "clickEventName", "dataTracking", "ariaDescribedBy", "onHeaderClick"]);
    const { current: descriptionId } = useRef(uuidv4());
    const { current: descriptionPeriodId } = useRef(uuidv4());
    const handleClick = (expanded) => {
        var _a;
        onExpandedChange(expanded);
        const name = clickEventName || `Accordion - ${heading}`;
        (_a = window.allytm) === null || _a === void 0 ? void 0 : _a.event('clickEvent', {
            name,
            tagName: 'button',
        });
    };
    return (React.createElement(AccordionHeader, Object.assign({ "$disableContainer": disableContainer }, rest),
        React.createElement(AccordionHeaderContainer, null,
            React.createElement(StyledRow, null,
                React.createElement(CustomButton, Object.assign({ onClick: (e) => {
                        handleClick(!isExpanded);
                        onHeaderClick === null || onHeaderClick === void 0 ? void 0 : onHeaderClick(e);
                    }, tabIndex: 0, "aria-expanded": isExpanded, "aria-describedby": `${ariaDescribedBy
                        ? `${ariaDescribedBy} ${descriptionPeriodId} `
                        : ''}${description ? descriptionId : ''}` || undefined, "data-testid": `accordionItem-heading-${accordionItemKey}`, "data-track-elem": "accordion", "data-track-trigger": (_b = dataTracking === null || dataTracking === void 0 ? void 0 : dataTracking.trigger) !== null && _b !== void 0 ? _b : 'statechange', "data-track-state": isExpanded ? 'opened' : 'closed', "data-track-name": (_c = dataTracking === null || dataTracking === void 0 ? void 0 : dataTracking.name) !== null && _c !== void 0 ? _c : heading }, ((dataTracking === null || dataTracking === void 0 ? void 0 : dataTracking.region)
                    ? { 'data-track-region': dataTracking === null || dataTracking === void 0 ? void 0 : dataTracking.region }
                    : {}), { type: "button" }),
                    React.createElement(ScreenReaderOnly, { id: descriptionPeriodId, "aria-hidden": true }, "."),
                    React.createElement(IconWrapper, null,
                        React.createElement(StyledIcon, { icon: deprecated.muiChevronRight, size: "xs", fill: "bluesuedeshoes", animateRotation: true, rotate: isExpanded ? 90 : 0, ariaHidden: true, "data-testid": `accordionIcon-expanded-${isExpanded}` })),
                    React.createElement(StyledHeading, { size: "md", ref: ref, tag: headingLevel }, addSuperscript(heading)))),
            description && (React.createElement(StyledTextBody, { tag: "p", size: "sm", id: descriptionId, "aria-hidden": true }, description)))));
};
export const AccordionItemHeaderBase = React.forwardRef(AccordionItemHeaderBaseRefForwarding);
//# sourceMappingURL=AccordionItemHeaderBase.js.map